import {
  Box,
  Grid,
  Pagination,
  Stack
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { genericMemo, type Props as BaseProps } from '.';
import { CircularProgress } from '../Progress';

type Props<T> = Required<Pick<BaseProps<T>, 'data' | 'gridSize' | 'renderItem' | 'totalPages' | 'isLoading'>> & {
  page: number;
  onPageChange: (page: number) => void;
}

// Memoized grid item that only re-renders when data changes
const MemoizedGridItem = memo(function GridItem<T>({
  item,
  renderItem,
}: {
  item: T;
  renderItem: (item: T) => React.ReactNode;
}) {
  return renderItem(item);
}) as <T>(props: {
  item: T;
  renderItem: (item: T) => React.ReactNode;
}) => React.ReactElement;

const ListItems = <T,>({
  data,
  renderItem,
  page,
  totalPages,
  gridSize,
  isLoading,
  onPageChange,
}: Props<T>) => {
  const [loadingPage, setLoadingPage] = useState<number | null>(null);

  // Handle page change with immediate loading state
  const handlePageClick = (page: number) => {
    setLoadingPage(page);
    onPageChange(page);
  };

  // Clear loading state when data loads or page changes
  useEffect(() => {
    if (!isLoading || loadingPage === page) {
      setLoadingPage(null);
    }
  }, [isLoading, page, loadingPage]);

  // Show loading state if we're waiting for a specific page
  const showLoadingForPage = loadingPage !== null && loadingPage !== page;

  return (
    <>
      {showLoadingForPage ? (
        <Stack alignItems="center" justifyContent="center" sx={{ marginTop: 8 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Grid container spacing={2}>
          {data.map((row, index) => (
            <Grid size={gridSize} key={index}>
              <MemoizedGridItem
                item={row}
                renderItem={renderItem}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
          }}
        >
          <Stack direction="row" justifyContent="center">
            <Box sx={{ p: 1, bgcolor: 'white', border: '1px solid black', borderRadius: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_event, page) => handlePageClick(page)}
                color="primary"
                siblingCount={0}
                boundaryCount={1}
              />
            </Box>
          </Stack>
        </Box>
      )}
    </>
  );
};

export default genericMemo(ListItems);
